import React from 'react';
import { Box, Typography, Paper, Link, Grid } from '@mui/material';
import SyntaxHighlighter from 'react-syntax-highlighter';
import atomOneDark from 'react-syntax-highlighter/dist/cjs/styles/hljs/atom-one-dark';
import SectionTitle from '../components/SectionTitle';
import { API_BASE_URL } from '../config';

// API Docs is always English regardless of the detected browser language.
// JSON examples are fully expanded (jq-style, one field per line).

const Json = ({ children }) => (
  <Box sx={{ minWidth: 0 }}>
    <SyntaxHighlighter
      language="json"
      style={atomOneDark}
      customStyle={{ borderRadius: 8, margin: 0, fontSize: '0.8rem', overflowX: 'auto' }}
    >
      {children}
    </SyntaxHighlighter>
  </Box>
);

const cardSx = { mb: 3, bgcolor: 'background.default', borderRadius: 2, border: '1px solid', borderColor: 'divider' };

const Endpoint = ({ method = 'GET', path, description, example }) => (
  <Paper elevation={0} sx={cardSx}>
    <Box sx={{ p: 2 }}>
      <Typography variant="subtitle1" fontWeight="bold" sx={{ fontFamily: 'monospace' }}>
        {method} {path}
      </Typography>
      <Typography variant="body2" sx={{ mb: 2, mt: 0.5 }} color="text.secondary">
        {description}
      </Typography>
      <Json>{example}</Json>
    </Box>
  </Paper>
);

const ZIP_NORMAL = `GET ${API_BASE_URL}/zip/1600023

{
  "postal_code": "1600023",
  "entries": [
    {
      "kanji": {
        "prefecture": "東京都",
        "city": "",
        "ward": "新宿区",
        "town": "西新宿",
        "block": "",
        "detail": "",
        "note": "（次のビルを除く）"
      },
      "katakana": {
        "prefecture": "トウキョウト",
        "city": "",
        "ward": "シンジュクク",
        "town": "ニシシンジュク",
        "block": "",
        "detail": ""
      },
      "romaji": {
        "prefecture": "Tokyo",
        "city": "",
        "ward": "Shinjuku-Ku",
        "town": "Nishishinjuku",
        "block": "",
        "detail": "",
        "note": "Area code excluding buildings that have their own postal code"
      },
      "is_building": false
    }
  ]
}`;

const ZIP_BUSINESS = `GET ${API_BASE_URL}/zip/1608336

{
  "postal_code": "1608336",
  "entries": [
    {
      "kanji": {
        "prefecture": "東京都",
        "city": "",
        "ward": "新宿区",
        "town": "西新宿",
        "block": "3丁目2-9",
        "detail": "",
        "note": ""
      },
      "katakana": {
        "prefecture": "トウキョウト",
        "city": "",
        "ward": "シンジュクク",
        "town": "ニシシンジュク",
        "block": "",
        "detail": ""
      },
      "romaji": {
        "prefecture": "Tokyo",
        "city": "",
        "ward": "Shinjuku-Ku",
        "town": "Nishishinjuku",
        "block": "3-2-9",
        "detail": "",
        "note": ""
      },
      "is_building": false,
      "business": {
        "kanji": "新宿ワシントンホテル",
        "katakana": "シンジユクワシントンホテル",
        "romaji": "Shinjiyukuwashintonhoteru",
        "office_kanji": "新宿",
        "is_pobox": false
      }
    }
  ]
}`;

const ApiDocsPage = () => (
  <Box>
    <SectionTitle>API Reference</SectionTitle>

    <Typography variant="body1" paragraph>
      All endpoints are <code>GET</code>, return{' '}
      <code>application/json; charset=utf-8</code>, and send{' '}
      <code>Access-Control-Allow-Origin: *</code>. Addresses are returned as
      grouped <code>kanji</code> / <code>katakana</code> / <code>romaji</code>{' '}
      fields — there is no pre-built address string; the client assembles it.
    </Typography>

    <Paper elevation={0} sx={{ p: 2, mb: 3, bgcolor: 'background.default', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
      <Typography variant="body2" color="text.secondary">Base URL</Typography>
      <Typography sx={{ fontFamily: 'monospace' }}>{API_BASE_URL}</Typography>
    </Paper>

    <Paper elevation={0} sx={{ p: 2, mb: 3, bgcolor: 'background.default', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
      <Typography variant="body2" color="text.secondary">Rate limit &amp; higher volume</Typography>
      <Typography variant="body2" sx={{ mt: 0.5 }}>
        <strong>5 requests/second.</strong> Need more? A $5/month plan covers most
        higher-volume usage — email{' '}
        <Link href="mailto:issues@generative-art-inc.com" color="secondary">
          issues@generative-art-inc.com
        </Link>{' '}
        to subscribe. Huge volumes by separate arrangement.
      </Typography>
    </Paper>

    <Typography variant="body1" paragraph>
      A single postal code can map to <strong>several entries</strong> (so{' '}
      <code>entries</code> is always an array). Most are general addresses; some
      are <strong>大口事業所 (large-volume business)</strong> records, which share
      the same schema and additionally carry a <code>business</code> block with
      the establishment name — compare the two <code>/zip</code> examples below
      (normal left, business right).
    </Typography>

    {/* /zip — normal (left) vs business (right). */}
    <Paper elevation={0} sx={cardSx}>
      <Box sx={{ p: 2 }}>
        <Typography variant="subtitle1" fontWeight="bold" sx={{ fontFamily: 'monospace' }}>
          GET /zip/:zipcode
        </Typography>
        <Typography variant="body2" sx={{ mb: 2, mt: 0.5 }} color="text.secondary">
          Exact postal-code lookup. A hyphen is accepted and stripped
          (160-0023 == 1600023). Returns every entry sharing the code. 404 when unknown.
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
              Normal address
            </Typography>
            <Json>{ZIP_NORMAL}</Json>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
              Business (大口事業所)
            </Typography>
            <Json>{ZIP_BUSINESS}</Json>
          </Grid>
        </Grid>
      </Box>
    </Paper>

    <Endpoint
      path="/reverse/:query"
      description="Free-word search across kanji, katakana and romaji fields plus the establishment name. The query is normalized (lowercased; hyphens / long-vowel marks / whitespace removed) and must match one whole field. `total` is the match count; `results` is capped (truncated=true when total exceeds it)."
      example={`GET ${API_BASE_URL}/reverse/Chiyoda

{
  "query": "Chiyoda",
  "total": 34,
  "truncated": false,
  "results": [
    {
      "postal_code": "1000001",
      "kanji": {
        "prefecture": "東京都",
        "city": "",
        "ward": "千代田区",
        "town": "千代田",
        "block": "",
        "detail": "",
        "note": ""
      },
      "katakana": {
        "prefecture": "トウキョウト",
        "city": "",
        "ward": "チヨダク",
        "town": "チヨダ",
        "block": "",
        "detail": ""
      },
      "romaji": {
        "prefecture": "Tokyo",
        "city": "",
        "ward": "Chiyoda-Ku",
        "town": "Chiyoda",
        "block": "",
        "detail": "",
        "note": ""
      },
      "is_building": false
    }
  ]
}`}
    />

    <Endpoint
      path="/meta"
      description="When the underlying dataset was last updated."
      example={`GET ${API_BASE_URL}/meta

{
  "dataLastModified": "2026-06-20T07:56:22.681230+00:00"
}`}
    />

    <Typography variant="body2" color="text.secondary">
      Data source: Japan Post (
      <Link href="https://www.post.japanpost.jp/zipcode/download.html" target="_blank" rel="noopener" color="secondary">
        japanpost.jp
      </Link>
      ).
    </Typography>
  </Box>
);

export default ApiDocsPage;
